import type { ReactNode } from "react";
import { Lockup } from "./Lockup.js";
import type { PortalBranding } from "./branding.js";

/**
 * Portal chrome: the partner lockup, who you are signed in as, the three sections, and a footer
 * that carries the NDPA notice and the DPO contact.
 *
 * Same restraint as `FormChrome` — brand header, content, footer, nothing else. No platform
 * branding and no "powered by" (docs/01-PRODUCT-SPEC.md §4.3).
 */

export type PortalTab = "applications" | "profile" | "data";

const TABS: { key: PortalTab; label: string }[] = [
  { key: "applications", label: "My applications" },
  { key: "profile", label: "My profile" },
  { key: "data", label: "My data" },
];

/**
 * The footer both the landing page and the signed-in shell use. `footer.text`, the privacy URL and
 * the DPO contact come from the platform brand profile, so Design → Brand profile is the one place
 * they are edited — but the NDPA sentence is not configurable, because it is an obligation (§14)
 * rather than a piece of copy.
 */
export const PortalFooter = ({ branding }: { branding: PortalBranding }): JSX.Element => (
  <footer className="sga-divider mt-12">
    <div className="mx-auto max-w-5xl px-4 py-8 text-xs sm:px-6">
      <p className="sga-muted max-w-2xl">
        Your data is processed under the Nigeria Data Protection Act 2023. You can download
        everything we hold about you, or ask for it to be erased, from “My data”.
      </p>
      <div className="sga-muted mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {branding.footer.text && <span>{branding.footer.text}</span>}
        {branding.footer.dpoContact && (
          <span>Data Protection Officer: {branding.footer.dpoContact}</span>
        )}
        {branding.footer.privacyUrl && (
          <a href={branding.footer.privacyUrl} className="sga-link text-xs">
            Privacy notice
          </a>
        )}
      </div>
    </div>
  </footer>
);

export const PortalShell = ({
  branding,
  email,
  active,
  onNavigate,
  onSignOut,
  children,
}: {
  branding: PortalBranding;
  email: string | null;
  active: PortalTab;
  onNavigate: (screen: PortalTab) => void;
  onSignOut: () => void;
  children: ReactNode;
}): JSX.Element => (
  <div className="sga-page">
    <header className="sga-header">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Lockup branding={branding} height="h-8" />

        {email && (
          <div className="flex items-center gap-3">
            {/* The address is the only "who am I signed in as" signal there is, so it stays
                visible — truncated on a narrow phone rather than dropped. */}
            <span className="sga-muted hidden max-w-[16rem] truncate text-xs sm:inline">
              {email}
            </span>
            <button
              type="button"
              onClick={onSignOut}
              className="sga-btn sga-btn--secondary sga-btn--sm"
            >
              Sign out
            </button>
          </div>
        )}
      </div>

      {email && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          {/* Scrolls rather than wraps: three tabs fit a 320px screen only just, and a wrapped
              second row shifts the whole page down for everyone else. */}
          <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Portal sections">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => onNavigate(tab.key)}
                aria-current={active === tab.key ? "page" : undefined}
                className="sga-tab"
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>

    <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">{children}</main>

    <PortalFooter branding={branding} />
  </div>
);
